"use client";

import { useState, useRef } from "react";
import Card from "@/components/ui/Card";
import { CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";

type Status = "idle" | "loading" | "success" | "error";

const PARTNER_TYPES = [
  "Freelance developer",
  "Agency / reseller",
  "Dev company",
  "Consultant or advisor",
  "Content creator",
  "Other",
] as const;

const inputClass =
  "w-full rounded-xl border border-border-subtle bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-neutral-500 transition-colors duration-150 focus:border-brand-purple focus:bg-white/[0.06] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50";

const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.1em] text-text-muted";

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <label className={labelClass}>
        {label}
        {required && <span className="ml-1 text-brand-purple-light">*</span>}
      </label>
      {children}
      {error && (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-red-400">
          <AlertCircle size={12} aria-hidden />
          {error}
        </p>
      )}
    </div>
  );
}

export default function AffiliateForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  function validate(data: Record<string, string>) {
    const errors: Record<string, string> = {};
    if (!data.name?.trim()) errors.name = "Full name is required.";
    if (!data.email?.trim()) {
      errors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = "Enter a valid email address.";
    }
    return errors;
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMsg("");

    const raw = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    const errors = validate(raw);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setStatus("loading");

    // Combine website + message into single message body
    const websiteLine = raw.website ? `Website / profile: ${raw.website}\n\n` : "";
    const body = {
      name: raw.name,
      email: raw.email,
      company: raw.company || "",
      service: raw.partnerType || "Affiliate",
      message: `${websiteLine}How I plan to refer clients:\n${raw.message}`,
      _hp: raw._hp || "",
    };

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json();

      if (!res.ok) {
        setErrorMsg(json.error ?? "Something went wrong. Please try again.");
        setStatus("error");
      } else {
        setStatus("success");
        formRef.current?.reset();
        setCharCount(0);
      }
    } catch {
      setErrorMsg("Network error. Check your connection and try again.");
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-brand-purple/20 bg-brand-purple/[0.06] px-8 py-14 text-center">
        <span
          className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border border-brand-purple/30 bg-brand-purple/10"
          role="status"
          aria-label="Success"
        >
          <CheckCircle2 size={28} className="text-brand-purple-light" />
        </span>
        <h3 className="mb-3 text-xl font-bold text-white">Application received</h3>
        <p className="text-sm leading-relaxed text-text-secondary">
          We review every application personally. You&apos;ll hear from us within 2 business days.
        </p>
      </div>
    );
  }

  return (
    <Card bordered className="mx-auto max-w-2xl">
      <form ref={formRef} className="space-y-5" onSubmit={handleSubmit} noValidate>
        {/* Honeypot — hidden from humans, bots fill it */}
        <input
          type="text"
          name="_hp"
          autoComplete="off"
          tabIndex={-1}
          aria-hidden="true"
          className="absolute h-0 w-0 overflow-hidden opacity-0"
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Full name" required error={fieldErrors.name}>
            <input
              type="text"
              name="name"
              autoComplete="name"
              required
              disabled={status === "loading"}
              placeholder="Jane Smith"
              className={inputClass}
              style={fieldErrors.name ? { borderColor: "rgba(248,113,113,0.5)" } : undefined}
            />
          </Field>

          <Field label="Email address" required error={fieldErrors.email}>
            <input
              type="email"
              name="email"
              autoComplete="email"
              required
              disabled={status === "loading"}
              placeholder="jane@example.com"
              className={inputClass}
              style={fieldErrors.email ? { borderColor: "rgba(248,113,113,0.5)" } : undefined}
            />
          </Field>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Company / organization">
            <input
              type="text"
              name="company"
              autoComplete="organization"
              disabled={status === "loading"}
              placeholder="Acme Labs (optional)"
              className={inputClass}
            />
          </Field>

          <Field label="Partner type">
            <select
              name="partnerType"
              disabled={status === "loading"}
              defaultValue=""
              className={inputClass + " cursor-pointer appearance-none"}
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center" }}
            >
              <option value="" disabled>Select your role…</option>
              {PARTNER_TYPES.map((t) => (
                <option key={t} value={t} className="bg-neutral-900 text-white">{t}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Website or social profile">
          <input
            type="url"
            name="website"
            autoComplete="url"
            disabled={status === "loading"}
            placeholder="https://yoursite.com (optional)"
            className={inputClass}
          />
        </Field>

        <Field label="How do you plan to refer clients?" error={fieldErrors.message}>
          <div className="relative">
            <textarea
              name="message"
              rows={5}
              maxLength={2000}
              disabled={status === "loading"}
              placeholder="Describe your audience, how you'd promote Instanodes, and any existing client relationships…"
              onChange={(e) => setCharCount(e.target.value.length)}
              className={inputClass + " resize-none"}
              style={fieldErrors.message ? { borderColor: "rgba(248,113,113,0.5)" } : undefined}
            />
            <span
              className="absolute bottom-3 right-4 text-[10px] tabular-nums text-neutral-600"
              aria-label={`${charCount} of 2000 characters`}
            >
              {charCount}/2000
            </span>
          </div>
        </Field>

        {status === "error" && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-xl border border-red-400/20 bg-red-400/[0.06] px-4 py-3"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-400" aria-hidden />
            <p className="text-sm text-red-400">{errorMsg}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={status === "loading"}
          className="flex w-full items-center justify-center gap-2 rounded-full border border-brand-purple bg-brand-purple px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:brightness-[0.92] hover:-translate-y-px active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {status === "loading" ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              Submitting…
            </>
          ) : (
            <>
              Submit application
              <ArrowRight size={16} aria-hidden />
            </>
          )}
        </button>

        <p className="text-center text-xs text-text-muted">
          We review every application. You&apos;ll hear back within 2 business days.
        </p>
      </form>
    </Card>
  );
}
